import ErrorIcon from '@mui/icons-material/Error'
import FilterListIcon from '@mui/icons-material/FilterList'
import LogoutIcon from '@mui/icons-material/Logout'
import ModeEditIcon from '@mui/icons-material/ModeEdit'
import React, { useRef } from 'react'
import { Helmet } from 'react-helmet'
import styled from 'styled-components'
import { Button as SutraButton } from 'sutra/components/Button'
import { Logo } from 'sutra/components/Logo/Logo'
import {
  Select as SutraSelect,
  type SelectOption,
} from 'sutra/components/Select/Select'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'
import { accents } from 'sutra/tokens/colors'

import { EmptyState } from 'components/empty-state/EmptyState'
import { SutraSearch } from 'components/ui/SutraSearch'
import { NotificationsContainer } from 'features/notifications'
import { EditProjectsModal } from 'features/projects/components/EditProjectsModal'
import { ProjectList } from 'features/projects/components/project-list/ProjectList'
import { ProjectFiltersModal } from 'features/projects/components/ProjectFiltersModal'
import { ExportDatasetsContainer } from 'features/projects/features/export'
import { BulkUpdatePayload } from 'features/projects/service/service'
import {
  getActiveFilterCount,
  ProjectFilters,
} from 'features/projects/utils/project-filters'
import { usePageKeyboardShortcuts } from 'hooks/keyboard-shortcuts'
import { TooltipIconButton } from 'src/components/TooltipIconButton'
import { PressableToggleButton } from 'src/components/ui/PressableToggleButton'
import { Project } from 'types/project'

// `search_type` is a wire value the API branches on (`/project/list` →
// `if search_type != "project"`), so the option VALUES stay as-is; only the
// labels say "hub".
const SEARCH_TYPE_OPTIONS: Array<SelectOption<string>> = [
  { value: 'project', label: 'Search by Hub' },
  { value: 'product', label: 'Search by Product' },
  { value: 'brand', label: 'Search by Brand' },
]

const SEARCH_TYPE_NOUNS: Record<string, string> = {
  project: 'hubs',
  product: 'products',
  brand: 'brands',
}

export interface HomeViewProps {
  projects: Project[]
  isLoading: boolean
  error: Error | null
  onRetry: () => void
  isAdmin: boolean
  isOwner: boolean
  searchType: string
  searchTerm: string
  onSearchTypeChange: (type: string) => void
  onSearchTermChange: (term: string) => void
  onSearchKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onDeleteProject: (uuid: string) => void
  onEditProjects: (patch: BulkUpdatePayload['body']) => Promise<void>
  onEditProject: (project: Project) => void
  onArchiveProject: (uuid: string) => void
  onInlineToggleInternal: (uuid: string, value: boolean) => void
  onSignOut: () => void
  onOpenAdmin: () => void
  modalOpened: boolean
  onOpenModal: () => void
  newProjectForm?: React.ReactNode
  highlightedIndex: number | null
  currentPage: number
  pageSize: number
  onPageChange: (page: number, pageSize: number) => void
  onTableChange: (currentDataSource: Project[]) => void
  selectedRowKeys: string[]
  onSelectedChange: (keys: string[]) => void
  editTarget: Project[] | null
  onCloseEditModal: () => void
  onOpenEditModal: () => void
  totalFilteredCount: number
  isEditLoading: boolean
  onSelectAll: () => void
  projectFilters: ProjectFilters
  onProjectFiltersChange: (filters: ProjectFilters) => void
  filtersModalOpen: boolean
  onOpenFiltersModal: () => void
  onCloseFiltersModal: () => void
  showRelativeTime: boolean
  onShowRelativeTimeChange: (value: boolean) => void
}

export const HomeView: React.FC<HomeViewProps> = ({
  projects,
  isLoading,
  error,
  onRetry,
  isAdmin,
  isOwner,
  searchType,
  searchTerm,
  onSearchTypeChange,
  onSearchTermChange,
  onSearchKeyDown,
  onDeleteProject,
  onEditProjects,
  onEditProject,
  onArchiveProject,
  onInlineToggleInternal,
  onSignOut,
  onOpenAdmin,
  modalOpened,
  onOpenModal,
  newProjectForm,
  highlightedIndex,
  currentPage,
  pageSize,
  onPageChange,
  onTableChange,
  selectedRowKeys,
  onSelectedChange,
  editTarget,
  onCloseEditModal,
  onOpenEditModal,
  totalFilteredCount,
  isEditLoading,
  onSelectAll,
  projectFilters,
  onProjectFiltersChange,
  filtersModalOpen,
  onOpenFiltersModal,
  onCloseFiltersModal,
  showRelativeTime,
  onShowRelativeTimeChange,
}) => {
  const activeFilterCount = getActiveFilterCount(projectFilters)
  const selectedCount = selectedRowKeys.length
  const showSelectAllLink =
    selectedCount > 0 && selectedCount < totalFilteredCount

  const MAX_TOOLTIP_TITLES = 5
  const selectedTitles = projects
    .filter((p) => selectedRowKeys.includes(p.uuid))
    .map((p) => p.title)
    .sort((a, b) => a.localeCompare(b))
  const visibleTitles = selectedTitles.slice(0, MAX_TOOLTIP_TITLES)
  const overflowCount = Math.max(0, selectedTitles.length - MAX_TOOLTIP_TITLES)

  const searchInputRef = useRef<HTMLInputElement>(null)

  usePageKeyboardShortcuts(
    { '/': () => searchInputRef.current?.focus() },
    { respectInputs: true },
  )

  if (error) {
    return (
      <EmptyState
        dataTestId="projects-error"
        icon={<LargeErrorIcon />}
        title="Failed to load hubs."
        description="Something went wrong loading your hubs. Try again in a moment."
        fullHeight="viewport"
        textWidth="300px"
        actions={[{ label: 'Try again', onClick: onRetry }]}
      />
    )
  }

  return (
    <HomePageWrapper>
      <Helmet>
        <meta name="robots" content="noindex" />
      </Helmet>
      <FixedHeader>
        <HeaderContent>
          <Logo $tone="primary" $height={32} />
        </HeaderContent>
      </FixedHeader>
      <ScrollableProjects>
        <ProjectsContent>
          <BulkActionRow>
            <PressableToggleButton
              pressed={showRelativeTime}
              onPressedChange={onShowRelativeTimeChange}
            >
              Relative timestamp
            </PressableToggleButton>
            <SutraButton
              variant="primary"
              size="sm"
              height="32px"
              onClick={() => onOpenFiltersModal()}
            >
              <SmallFilterListIcon />
              {activeFilterCount > 0
                ? `Filters (${activeFilterCount})`
                : 'Filters'}
            </SutraButton>
            <ExportDatasetsContainer pageFilters={projectFilters} />
            {isAdmin && (
              <>
                <Tooltip
                  content={
                    selectedCount === 0 ? (
                      <TooltipText>Select projects to edit</TooltipText>
                    ) : (
                      <TooltipContent>
                        <TooltipProjectList>
                          {visibleTitles.map((title) => (
                            <div key={title}>{title}</div>
                          ))}
                          {overflowCount > 0 && (
                            <div>And {overflowCount} more</div>
                          )}
                        </TooltipProjectList>
                      </TooltipContent>
                    )
                  }
                >
                  <TooltipButtonWrapper>
                    <SutraButton
                      variant="secondary"
                      size="sm"
                      height="32px"
                      disabled={selectedCount === 0}
                      onClick={() => onOpenEditModal()}
                    >
                      <SmallModeEditIcon />
                      {selectedCount > 0 ? `Edit (${selectedCount})` : 'Edit'}
                    </SutraButton>
                  </TooltipButtonWrapper>
                </Tooltip>
                {showSelectAllLink && (
                  <SelectAllLink onClick={onSelectAll}>
                    Select all {totalFilteredCount} hubs
                  </SelectAllLink>
                )}
              </>
            )}
            <Controls>
              <SearchTypeSelectWrap>
                <SutraSelect
                  value={searchType}
                  onChange={(picked) => onSearchTypeChange(picked)}
                  options={SEARCH_TYPE_OPTIONS}
                />
              </SearchTypeSelectWrap>
              <SearchInput
                ref={searchInputRef}
                autoFocus
                placeholder={`Search ${SEARCH_TYPE_NOUNS[searchType] ?? searchType}...`}
                value={searchTerm}
                onChange={(e) => onSearchTermChange(e.target.value)}
                onKeyDown={onSearchKeyDown}
                shortcutHint="/"
              />
              {isAdmin && (
                <SutraButton
                  variant="primary"
                  size="sm"
                  height="32px"
                  onClick={() => onOpenModal()}
                >
                  Create dataset
                </SutraButton>
              )}
              {isOwner && (
                <SutraButton
                  variant="secondary"
                  size="sm"
                  height="32px"
                  onClick={onOpenAdmin}
                >
                  Admin
                </SutraButton>
              )}
              {/*
                Bell rides inline here on the projects-list page so it
                sits next to the other top-right controls instead of
                floating as a fixed overlay above them. ``SignedInLayout``
                suppresses its overlay for ``/`` so we don't show two
                bells.
              */}
              <NotificationsContainer />
              <TooltipIconButton
                tooltipTitle="Signout"
                icon={<LogoutIcon />}
                onClick={onSignOut}
                height={32}
                width={32}
              />
            </Controls>
          </BulkActionRow>
          <ProjectList
            projects={projects}
            loading={isLoading}
            handleRemoveProject={onDeleteProject}
            onEditProject={onEditProject}
            onArchiveProject={onArchiveProject}
            onInlineToggleInternal={onInlineToggleInternal}
            onSelectedChange={onSelectedChange}
            selectedRowKeys={selectedRowKeys}
            isAdmin={isAdmin}
            highlightedIndex={highlightedIndex}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={onPageChange}
            onTableChange={onTableChange}
            showRelativeTime={showRelativeTime}
          />
        </ProjectsContent>
      </ScrollableProjects>
      {modalOpened && newProjectForm}
      <ProjectFiltersModal
        open={filtersModalOpen}
        applied={projectFilters}
        onClose={onCloseFiltersModal}
        onApply={onProjectFiltersChange}
      />
      {editTarget && editTarget.length > 0 && (
        <EditProjectsModal
          open
          projects={editTarget}
          loading={isEditLoading}
          onClose={onCloseEditModal}
          onApply={onEditProjects}
        />
      )}
    </HomePageWrapper>
  )
}

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
`

const SmallFilterListIcon = styled(FilterListIcon)`
  font-size: 14px;
`

const SmallModeEditIcon = styled(ModeEditIcon)`
  font-size: 12px;
`

const TooltipText = styled.span`
  font-size: 12px;
`

const TooltipContent = styled.div`
  font-size: 12px;
  text-align: center;
`

const TooltipProjectList = styled.div`
  display: inline-block;
  text-align: left;
`

const TooltipButtonWrapper = styled.span`
  display: inline-block;
`

const SearchTypeSelectWrap = styled.div`
  width: 170px;
  flex-shrink: 0;

  & > button {
    height: 32px;
  }
`

const SearchInput = styled(SutraSearch)`
  width: 200px;
`

const HomePageWrapper = styled.div`
  height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  color: var(--color-text-black);
  font-weight: 400;
  padding: 24px;
  background: white;
`

const FixedHeader = styled.div`
  flex-shrink: 0;
  width: 100%;
  background: #ffffff;
`

const HeaderContent = styled.div`
  margin: 0 auto;
  padding: 0 0 12px 0;
  display: flex;
  align-items: flex-start;
`

const ScrollableProjects = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #ffffff;
`

const ProjectsContent = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
`

const LargeErrorIcon = styled(ErrorIcon)`
  font-size: 48px;
`

const BulkActionRow = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  /* Room for Input's 4px outward focus ring, which ScrollableProjects'
     overflow:hidden would otherwise clip on this first-child row. */
  padding-top: 4px;
  margin-bottom: 8px;
`

const SelectAllLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: ${accents.reflectionBlue[500]};
  cursor: pointer;
  font-size: 13px;
  text-decoration: underline;

  &:hover {
    color: ${accents.reflectionBlue[600]};
  }
`
